<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <loading-spinner v-if="isLoading"></loading-spinner>

        <paginated-table
          v-else
          :title="$t('title.submission_status')"
          icon="manage_search"
          :columns="columns"
          :data="papers"
          @updatePagination="updatePagination"
        >
          <template v-slot:top-buttons>
            <custom-button
              @click="toSubmissions"
              :label="$t('button.add')"
              color="primary"
              icon="add"
              size="lg"
              gradient
              rounded
            ></custom-button>
          </template>
        </paginated-table>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";

import { getPaperStatusColumns } from "@/fields/paper/columns";
import { deletePaperPopup } from "@/fields/paper/popups";

import { paperApi } from "@/api/api";

export default {
  components: {
    PaginatedTable,
    LoadingSpinner,
    CustomButton,
  },
  data() {
    return {
      papers: {
        page_info: {
          page: 0,
          size: 0,
          total_elements: 0,
          total_pages: 0,
        },
        elements: [],
      },
      isLoading: false,
      columns: getPaperStatusColumns({
        edit: this.handleEdit,
        delete: this.handleDelete,
        showDetail: this.handleShowPaperDetail,
      }),
    };
  },
  methods: {
    /**
     * ******************************************************************************************************************
     *  기타 메서드
     * ******************************************************************************************************************
     */
    toSubmissions() {
      this.$router.push("/submissions/create/journal");
    },
    toEditPaper(paper) {
      this.$router.push({
        name: "submission-edit",
        params: {
          idx: paper.idx,
        },
      });
    },
    toPaperDetail(paper) {
      this.$router.push({
        name: "submission-detail",
        params: {
          idx: paper.idx,
        },
      });
    },
    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수
     * @property {string} total_pages - 전체 페이지 수
     */

    /**
     * 페이지네이션 업데이트 메서드
     *
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.papers.page_info.size = pagination.size;
      this.papers.page_info.page = pagination.page;

      this.fetchPapers(this.papers.page_info.size, this.papers.page_info.page);
    },
    /**
     * ******************************************************************************************************************
     *  api 요청
     * ******************************************************************************************************************
     */
    async fetchPapers(size, page) {
      const params = {
        page: page ? page : 1,
        size: size ? size : 5,
        sort_by: "idx",
        direction: "desc",
      };

      try {
        this.isLoading = true;
        const response = await paperApi.getPapersByMember(params);
        this.papers = response?.data?.response;
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        this.$router.push({ name: "dashboard" });
      } finally {
        this.isLoading = false;
      }
    },

    async deletePaper(paper) {
      this.isLoading = true;
      try {
        const response = await paperApi.deletePaper(paper);
        if (response && response.status === 204) {
          return true;
        }
        return false;
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * ******************************************************************************************************************
     *  버튼 액션
     * ******************************************************************************************************************
     */

    // 논문 삭제
    async handleDelete(paper) {
      if (paper.category !== "SUBMISSION") {
        this.notifyVue({
          message: this.$t("alert.can_not_delete_paper_status"),
          horizontal: "center",
          style: "danger",
        });
        return false;
      }

      const isConfirmed = await deletePaperPopup(paper);
      if (!isConfirmed) {
        return;
      }

      const isDeleted = await this.deletePaper(paper);
      if (isDeleted) {
        await this.fetchPapers(
          this.papers.page_info.size,
          this.papers.page_info.page
        );
        this.notifyVue({
          message: this.$t("alert.success_request"),
          horizontal: "center",
          style: "success",
        });
      }
    },
    async handleEdit(paper) {
      if (
        !["SUBMISSION", "REVISION", "FINAL_REVISION"].includes(
          paper.category
        ) ||
        paper.status.includes("COMPLETED")
      ) {
        this.notifyVue({
          message: this.$t("alert.can_not_edit_paper"),
          horizontal: "center",
          style: "danger",
        });
        return;
      }
      await this.toEditPaper(paper);
    },
    async handleShowPaperDetail(paper) {
      await this.toPaperDetail(paper);
    },

    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  mounted() {
    this.fetchPapers(this.papers.page_info.size, this.papers.page_info.page);
  },
};
</script>
<style scoped></style>
